import { FC, SyntheticEvent } from 'react'
import { useIntl } from 'react-intl'
import ResultViewer from '../../utils/ResultViewer/ResultViewer'
import Filters from '../../utils/ResultViewer/Filters'
import Pagination from '../../utils/ResultViewer/Pagination'
import axios from 'axios'
import ProfileChart from './ProfileChart'

type WrapperProps = {
  onEdit: (id: any, refresh: Function) => void
  onDelete: (id: any, refresh: Function) => void
  onAdd?: (event: SyntheticEvent, refresh: Function) => void
  enablePolling?: boolean
  pollingInterval?: number
}

class ProfilesResultTable extends ResultViewer {
  private pollingTimer?: NodeJS.Timeout
  private lastDataHash?: string

  constructor(props: any) {
    super(props)
    this.handleRetry = this.handleRetry.bind(this)
    this.pollingTimer = undefined
    this.lastDataHash = undefined
  }

  componentDidMount() {
    super.componentDidMount()
    const { enablePolling = false, pollingInterval = 5000 } = this.props as any
    if (enablePolling) {
      this.startPolling(pollingInterval)
    }
  }

  componentWillUnmount() {
    this.stopPolling()
  }

  startPolling(interval: number) {
    this.stopPolling()
    this.pollingTimer = setInterval(() => {
      this.pollData()
    }, interval)
  }

  stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
      this.pollingTimer = undefined
    }
  }

  hashData(data: any): string {
    // Simple hash of data for comparison
    return JSON.stringify(data?.data || [])
  }

  pollData() {
    axios
      .get(this.props.dataUrl, {
        withCredentials: true,
        params: {
          page: this.state.page,
          orderBy: this.state.orderBy,
          orderDirection: this.state.orderDirection,
          ...this.state.filterParams,
        },
      })
      .then((response) => {
        let data = response.data.data
        const newHash = this.hashData(data)

        // Only update if data has changed
        if (this.lastDataHash !== newHash) {
          this.lastDataHash = newHash
          data.search = Object.values(data.search)
          this.setState({
            data: data,
          })
        }
      })
      .catch((error) => {
        console.error('Polling error:', error)
      })
  }

  openChart(row: any) {
    const status = row['status']
    const id = row['edit_actions']
    if (!(status === 'active' || status === 'passive')) {
      return
    }
    axios.get('/profiles/single/' + id).then((response) => {
      const data = response.data?.data ?? {}
      const profileData = data.profile_data
      const sourceData = data.profile_data_source
      try {
        const parsed = typeof profileData === 'string' ? JSON.parse(profileData) : profileData || {}
        const entries = Object.entries(parsed as Record<string, string>)
        const sorted = entries.sort((a: any, b: any) => {
          const toMin = (t: string) => {
            const [hh, mm] = t.split(':').map((x) => parseInt(x, 10))
            return hh * 60 + mm
          }
          return toMin(a[0]) - toMin(b[0])
        })
        const categories = sorted.map((e: any) => e[0])
        const series = sorted.map((e: any) => parseFloat(e[1]))
        ;(this.state as any)._chartOpen = true
        ;(this.state as any)._chartCategories = categories
        ;(this.state as any)._chartSeries = series
        ;(this.state as any)._chartTitle = row['name'] || 'Profile'
        ;(this.state as any)._chartSource = sourceData
        this.setState({} as any)
      } catch (e) {
        ;(this.state as any)._chartOpen = true
        ;(this.state as any)._chartCategories = []
        ;(this.state as any)._chartSeries = []
        ;(this.state as any)._chartTitle = row['name'] || 'Profile'
        ;(this.state as any)._chartSource = sourceData
        this.setState({} as any)
      }
    })
  }

  render() {
    let limit = this.state.data?.pagination.limit ?? 0
    let page = this.state.page ?? 0
    let totalCount = this.state.data?.pagination.total_count ?? 0
    let viewStart = (page - 1) * limit
    if (viewStart === 0) {
      viewStart = 1
    } else {
      viewStart = viewStart + 1
    }
    let viewEnd = page * limit
    if (viewEnd > totalCount) {
      viewEnd = totalCount
    }
    if (!this.state.data) {
      return <>Loading...</>
    } else {
      return (
        <>
          <div className='card mb-5 mb-xl-8'>
            <div className='card-header'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bold fs-3 mb-1'>{this.props.title}</span>
                <span className='text-muted mt-1 fw-semibold fs-7'>
                  Out of {this.state.data?.pagination.total_count ?? 0} records {viewStart} -&nbsp;
                  {viewEnd ?? 1} is monitoring.
                </span>
              </h3>
              <div className='align-self-end align-self-center'>
                <Filters
                  search={this.state.data.search ?? []}
                  setFilterParams={(name: string, value: any) => {
                    this.setFilterParams(name, value)
                  }}
                  applyFilter={() => {
                    this.fetchData()
                  }}
                  clearFilters={() => {
                    this.clearFilterParams()
                  }}
                />
                {this.state.actionItems?.map((item, index) => {
                  return (
                    <button
                      key={index}
                      className={item.class}
                      onClick={(event) => {
                        item.onClick(event, this.fetchData)
                      }}
                    >
                      <span className={item.icon + ' me-2'}></span>
                      {item.text}
                    </button>
                  )
                })}
              </div>
            </div>
            <div className='card-body overflow-auto'>
              <table className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'>
                <thead>
                  <tr>{this.renderHeaders()}</tr>
                </thead>
                <tbody>{this.renderRows()}</tbody>
              </table>
            </div>
            <div className='card-footer'>
              <Pagination {...this.state.data.pagination} setPage={this.setPage} />
            </div>
          </div>
          {(this.state as any)._chartOpen && (
            <ProfileChart
              show={(this.state as any)._chartOpen}
              title={(this.state as any)._chartTitle}
              categories={(this.state as any)._chartCategories || []}
              series={(this.state as any)._chartSeries || []}
              source={(this.state as any)._chartSource}
              onHide={() => {
                ;(this.state as any)._chartOpen = false
                this.setState({} as any)
              }}
            />
          )}
        </>
      )
    }
  }

  renderHeaders() {
    if (this.state.data) {
      return Object.keys(this.state.data.headers).map((key) => {
        return this.renderHeader(key)
      })
    } else {
      return <></>
    }
  }

  renderHeader(key: string) {
    // Hide event_type_name column header
    if (key === 'event_type_name') {
      return <th key={key} style={{ display: 'none' }}></th>
    }
    if (isNaN(parseInt(key))) {
      let direction =
        this.state.orderBy === key && this.state.orderDirection === 'DESC' ? 'ASC' : 'DESC'
      return (
        <th
          key={key}
          role='button'
          onClick={() => this.setOrderBy(key, direction)}
          className='pe-5'
        >
          <div className='d-flex justify-content-between'>
            {(this.state.data?.headers as any)[key]}
            <span
              className={
                'fa ' +
                (this.state.orderBy === key && direction === 'ASC'
                  ? 'fa-arrow-down'
                  : 'fa-arrow-up')
              }
            ></span>
          </div>
        </th>
      )
    }
    return <th key={key}>{(this.state.data?.headers as any)[key]}</th>
  }

  renderRows() {
    if (this.state.data) {
      return this.state.data.data.map((row: any, index) => {
        return (
          <tr key={index}>
            {Object.keys(row).map((key, colIndex) => {
              return this.renderRow(row, key, colIndex)
            })}
          </tr>
        )
      })
    } else {
      return <></>
    }
  }

  handleRetry(profileId: string) {
    // Get CSRF token and update status to 'created'
    axios.get('/profiles/save/' + profileId).then((response) => {
      const csrfData = response.data.data
      axios
        .post('/profiles/save/' + profileId, {
          ...csrfData,
          profiles: {
            status: 'created',
          },
        })
        .then(() => {
          this.fetchData()
        })
        .catch((error) => {
          console.error('Failed to retry profile:', error)
        })
    })
  }

  renderRow(row: any, key: string, colIndex: number) {
    if (key === 'edit_actions') {
      const canChart = row['status'] === 'active' || row['status'] === 'passive'
      const isFailed = row['status'] === 'failed'
      return (
        <td key={colIndex}>
          <div className='d-flex justify-content-end flex-shrink-0'>
            {isFailed ? (
              <button
                onClick={() => this.handleRetry(row[key])}
                className='btn btn-icon btn-bg-light btn-active-color-warning btn-sm me-1'
                title='Retry processing'
              >
                <span className='fa fa-redo'></span>
              </button>
            ) : (
              <button
                onClick={() => this.openChart(row)}
                className='btn btn-icon btn-bg-light btn-active-color-info btn-sm me-1'
                disabled={!canChart}
                title={canChart ? 'View chart' : 'Chart available only for active/passive'}
              >
                <span className='fa fa-chart-line'></span>
              </button>
            )}
            <button
              onClick={() => this.props.editCallback(row[key], this.fetchData)}
              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
            >
              <span className='fa fa-edit'></span>
            </button>
            <button
              onClick={() => this.props.deleteCallback(row[key], this.fetchData)}
              className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm me-1'
            >
              <span className='fa fa-trash'></span>
            </button>
          </div>
        </td>
      )
    }
    if (key === 'profile_type') {
      const type = row[key]
      const label =
        type === 'event_based' ? 'Event Based' : type === 'day_based' ? 'Day Based' : String(type)
      const badgeClass =
        type === 'event_based'
          ? 'badge badge-info'
          : type === 'day_based'
          ? 'badge badge-warning'
          : 'badge badge-light'
      return (
        <td key={colIndex}>
          <span className={badgeClass}>{label}</span>
        </td>
      )
    }
    if (key === 'status') {
      const status = row[key]
      const label =
        status === 'created'
          ? 'Queued'
          : status === 'preparing'
          ? 'Preparing'
          : status === 'active'
          ? 'Active'
          : status === 'passive'
          ? 'Passive'
          : status === 'failed'
          ? 'Failed'
          : String(status)
      const badgeClass =
        status === 'created'
          ? 'badge badge-warning'
          : status === 'preparing'
          ? 'badge badge-info'
          : status === 'active'
          ? 'badge badge-success'
          : status === 'passive'
          ? 'badge badge-secondary'
          : status === 'failed'
          ? 'badge badge-danger'
          : 'badge badge-light'
      return (
        <td key={colIndex}>
          <span className={badgeClass}>{label}</span>
        </td>
      )
    }
    // Show only the relevant detail column
    if (key === 'day') {
      if (row['profile_type'] !== 'day_based') {
        return <td key={colIndex}></td>
      }
      return <td key={colIndex} dangerouslySetInnerHTML={{ __html: row[key] }}></td>
    }
    if (key === 'event_type') {
      if (row['profile_type'] !== 'event_based') {
        return <td key={colIndex}></td>
      }
      // Use event_type_name for display instead of event_type
      const displayValue = row['event_type_name'] || row[key]
      return <td key={colIndex} dangerouslySetInnerHTML={{ __html: displayValue }}></td>
    }
    // Hide event_type_name column (it's used in event_type display)
    if (key === 'event_type_name') {
      return <td key={colIndex} style={{ display: 'none' }}></td>
    }
    return (
      <td
        key={colIndex}
        dangerouslySetInnerHTML={{
          __html: row[key],
        }}
      ></td>
    )
  }
}

const ProfilesTable: FC<WrapperProps> = ({
  onEdit,
  onDelete,
  onAdd,
  enablePolling = false,
  pollingInterval = 5000,
}) => {
  const intl = useIntl()
  return (
    <ProfilesResultTable
      title={intl.formatMessage({ id: 'MENU.PROFILES' })}
      dataUrl='/profiles/list'
      editCallback={onEdit}
      deleteCallback={onDelete}
      {...({ enablePolling, pollingInterval } as any)}
      actionItems={
        onAdd
          ? [
              {
                text: intl.formatMessage({
                  id: 'MENU.PROFILES.ADD_PROFILE',
                  defaultMessage: 'Add Profile',
                }),
                class: 'btn btn-sm btn-primary',
                icon: 'fa fa-plus',
                onClick: onAdd,
              },
            ]
          : []
      }
    />
  )
}

export default ProfilesTable
